import React, { useMemo, useEffect, useRef, useState } from "react";

/**
 * SpinWheel – SVG-basert lykkehjul med rotasjons-animasjon.
 *
 * Bakoverkompatibel props:
 *   segments: [{id, label, auto_respin?}]
 *   spinningTo: null | number (legacy enkelt spinn – beholder seg)
 *   onSpinComplete: () => void
 *   size: 320 (default)
 *   theme: { primary, accent, text } – matcher skapersidens tema
 *
 * Nye props for respin-kjede:
 *   spinSequence: null | number[]  – array av segment-indekser å animere etter hverandre
 *   onSegmentReached: (index, isLastInSequence) => void  – kalles når hjulet stopper på hvert ledd
 *   respinPauseMs: 1200 – ventetid på respin-segmenter før neste spinn starter
 */
const PALETTE_LIGHT = ["#D11A5B", "#D4A574", "#7FA88E", "#5FA59E", "#B26A3F", "#9B7BB8", "#5BA5C9", "#C7A8C7"];
const PALETTE_DARK = ["#A11247", "#A8825D", "#5E8870", "#4A857F", "#8A5230", "#7A5C92", "#4585A5", "#A185A1"];

const SINGLE_SPIN_TURNS = 6;       // antall ekstra runder per spinn
const RESPIN_TURNS = 3;            // mellom-spinn (kortere for å holde flyt)

export default function SpinWheel({
  segments = [],
  spinningTo = null,
  onSpinComplete,
  size = 320,
  theme,
  spinSequence = null,
  onSegmentReached,
  respinPauseMs = 1200,
}) {
  const n = segments.length;
  const r = size / 2;
  const cx = r, cy = r;
  const anglePerSeg = n > 0 ? 360 / n : 0;

  // Akkumulert rotasjon når vi animerer en kjede (legges på toppen av forrige)
  const [chainRotation, setChainRotation] = useState(null);
  const [chainStep, setChainStep] = useState(0);
  const cumulativeRotationRef = useRef(0);

  // Beregn rotasjon for enkelt spinn (legacy modus)
  const singleRotation = useMemo(() => {
    if (spinningTo === null || n === 0) return 0;
    const targetAngle = spinningTo * anglePerSeg + anglePerSeg / 2;
    return -(targetAngle) + 360 * SINGLE_SPIN_TURNS;
  }, [spinningTo, n, anglePerSeg]);

  // Start kjede-animasjon når spinSequence settes
  useEffect(() => {
    if (!spinSequence || spinSequence.length === 0 || n === 0) {
      setChainRotation(null);
      setChainStep(0);
      cumulativeRotationRef.current = 0;
      return;
    }
    // Start fra step 0
    setChainStep(0);
    // Beregn rotasjon for første ledd
    const first = spinSequence[0];
    const targetAngle = first * anglePerSeg + anglePerSeg / 2;
    // Normaliser nåværende rotasjon mod 360 og legg til ekstra runder + target
    const currentMod = ((cumulativeRotationRef.current % 360) + 360) % 360;
    const desiredMod = ((-targetAngle) % 360 + 360) % 360;
    // Antall grader vi må snurre fra current til desired, med X ekstra runder
    let delta = desiredMod - currentMod;
    if (delta > 0) delta -= 360;  // alltid roter bakover (med klokken visuelt)
    const turns = spinSequence.length === 1 ? SINGLE_SPIN_TURNS : RESPIN_TURNS;
    const target = cumulativeRotationRef.current + delta - 360 * turns;
    cumulativeRotationRef.current = target;
    setChainRotation(target);
  }, [spinSequence, n, anglePerSeg]);

  const onTransitionEnd = () => {
    if (spinSequence && spinSequence.length > 0) {
      const isLast = chainStep >= spinSequence.length - 1;
      if (onSegmentReached) onSegmentReached(spinSequence[chainStep], isLast);
      if (isLast) {
        if (onSpinComplete) onSpinComplete();
        return;
      }
      // Vent litt så brukeren ser respin-segmentet, så start neste ledd
      const nextStep = chainStep + 1;
      const nextTarget = spinSequence[nextStep];
      setTimeout(() => {
        const targetAngle = nextTarget * anglePerSeg + anglePerSeg / 2;
        const currentMod = ((cumulativeRotationRef.current % 360) + 360) % 360;
        const desiredMod = ((-targetAngle) % 360 + 360) % 360;
        let delta = desiredMod - currentMod;
        if (delta > 0) delta -= 360;
        const turns = nextStep === spinSequence.length - 1 ? SINGLE_SPIN_TURNS : RESPIN_TURNS;
        const target = cumulativeRotationRef.current + delta - 360 * turns;
        cumulativeRotationRef.current = target;
        setChainStep(nextStep);
        setChainRotation(target);
      }, respinPauseMs);
    } else if (spinningTo !== null && onSpinComplete) {
      onSpinComplete();
    }
  };

  if (n === 0) {
    return (
      <div style={{ width: size, height: size, display: "grid", placeItems: "center" }}>
        <span className="font-meta text-xs text-[var(--cream-faint)]">Ingen segmenter</span>
      </div>
    );
  }

  // Pikk rotasjon: kjede-modus tar prioritet over legacy
  const activeRotation = chainRotation !== null ? chainRotation : singleRotation;
  const isAnimating = chainRotation !== null || spinningTo !== null;
  // Lengre overgangstid for siste ledd (det stopper på premien) enn for respin-ledd
  const isLastInChain = spinSequence && chainStep >= spinSequence.length - 1;
  const transitionMs = chainRotation !== null
    ? (isLastInChain ? 4500 : 2500)
    : 4500;

  return (
    <div className="relative inline-block" style={{ width: size, height: size }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        style={{
          transform: `rotate(${activeRotation}deg)`,
          transition: isAnimating ? `transform ${transitionMs}ms cubic-bezier(0.17, 0.67, 0.12, 0.99)` : "none",
        }}
        onTransitionEnd={onTransitionEnd}
      >
        <defs>
          <filter id="wheel-shadow" x="-10%" y="-10%" width="120%" height="120%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000" floodOpacity="0.5"/>
          </filter>
        </defs>
        <g filter="url(#wheel-shadow)">
          {segments.map((seg, i) => {
            const startAngle = i * anglePerSeg - 90;
            const endAngle = (i + 1) * anglePerSeg - 90;
            const startRad = (startAngle * Math.PI) / 180;
            const endRad = (endAngle * Math.PI) / 180;
            const x1 = cx + r * Math.cos(startRad);
            const y1 = cy + r * Math.sin(startRad);
            const x2 = cx + r * Math.cos(endRad);
            const y2 = cy + r * Math.sin(endRad);
            const largeArc = anglePerSeg > 180 ? 1 : 0;
            const fill = (i % 2 === 0 ? PALETTE_LIGHT : PALETTE_DARK)[Math.floor(i / 2) % PALETTE_LIGHT.length];

            const textAngle = startAngle + anglePerSeg / 2;
            const textRad = (textAngle * Math.PI) / 180;
            const innerR = r * 0.18;
            const outerR = r * 0.95;
            const availableLen = outerR - innerR;

            const tx = cx + outerR * Math.cos(textRad);
            const ty = cy + outerR * Math.sin(textRad);

            const fontSize = Math.max(6, Math.min(11, 160 / n));
            const truncated = seg.label.length > 24 ? seg.label.slice(0, 23) + "…" : seg.label;

            return (
              <g key={seg.id || i}>
                <path
                  d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                  fill={fill}
                  stroke="rgba(0,0,0,0.4)"
                  strokeWidth="1.5"
                />
                <text
                  x={tx}
                  y={ty}
                  fill="#0A0A0A"
                  fontSize={fontSize}
                  fontFamily="Manrope, sans-serif"
                  fontWeight="600"
                  textAnchor="start"
                  dominantBaseline="central"
                  textLength={availableLen}
                  lengthAdjust="spacingAndGlyphs"
                  transform={`rotate(${textAngle + 180} ${tx} ${ty})`}
                  style={{ pointerEvents: "none" }}
                >
                  {truncated}
                </text>
              </g>
            );
          })}
        </g>
        <circle cx={cx} cy={cy} r={r * 0.12} fill="#0A0A0A" stroke={theme?.accent || "#D11A5B"} strokeWidth="3" />
      </svg>

      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1"
        style={{
          width: 0,
          height: 0,
          borderLeft: "14px solid transparent",
          borderRight: "14px solid transparent",
          borderTop: `22px solid ${theme?.accent || "#D11A5B"}`,
          filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))",
        }}
      />
    </div>
  );
}
